import type { RemovedPart, TapeEntry } from "@typedoc/interview/events";
import { questionOriginSchema } from "@typedoc/interview/events";
import { replay, type Session } from "@typedoc/interview/session";
import { z } from "zod";
import {
  CAPTURE_TRIGGER_REASONS,
  PAGE_ADDRESS_MAX,
  screenContextSchema,
  screenViewSchema,
  traceActionSchema,
  type ScreenView,
  type TraceEvent,
} from "./capture";
import { idSchema } from "./ids";
import { recordingViewSchema, type RecordingView } from "./recordings";

/**
 * A recording's package (ENG-1028): everything the recording produced, laid out on one clock, so
 * the package view draws it as a timeline and the chat's agent reads it as a source. It folds three
 * records the API already keeps: the interview tape (what was said and asked, pauses, covered
 * fields, stretches not shared), the kept screens, and the light action trace (apps and pages, and
 * what the person did). Nothing here is stored; `foldPackage` makes it on every read, here and in
 * the sample build alike, as `replay` does for the island.
 *
 * Every time is epoch milliseconds. The clock runs from `startedAt` (Start, after the person chose
 * what Abacor sees) to `endsAt`; a reader shows a time as its distance from `startedAt`.
 *
 * What the person changed after the end (ENG-1029) is folded in too: a note added later is a
 * second kind of `said` line, a corrected answer follows the one it corrects, and a removed part is
 * left out everywhere (a screen, an action, a line, an app or page in a removed stretch, and every
 * answer Abacor took from a removed input), with each removal listed in `removals`.
 *
 * This module is browser-safe: it imports nothing from Node or a server.
 */

const atSchema = z.int().nonnegative();

/** A kept screen with why it was kept, in words, and its flag (D5). */
export const packageScreenSchema = screenViewSchema.extend({
  reason: z.string().min(1),
  /** Its fields could not be checked in time: it may show something sensitive. */
  flagged: z.boolean(),
});
export type PackageScreen = z.infer<typeof packageScreenSchema>;

/** A stretch in one app, window and page, from the trace (or from the screens, before it). */
export const appSpanSchema = z.strictObject({
  from: atSchema,
  to: atSchema,
  app: screenContextSchema.shape.app,
  window: screenContextSchema.shape.window,
  /** The page's full address (D8), or null outside a browser or before it was read. */
  page: z.string().min(1).max(PAGE_ADDRESS_MAX).nullable(),
});
export type AppSpan = z.infer<typeof appSpanSchema>;

/** Something the person did: a click, a field, a copy or a download, never a value. */
export const packageActionSchema = z.strictObject({
  id: idSchema("recordingTrace"),
  at: atSchema,
  /** The screen kept last before it, when there was one. */
  screenId: idSchema("recordingScreen").nullable(),
  event: traceActionSchema,
});
export type PackageAction = z.infer<typeof packageActionSchema>;

/** What the person said or typed, in their own words. */
export const saidLineSchema = z.strictObject({
  kind: z.literal("said"),
  /** The tape entry it came in. */
  seq: z.int().positive(),
  at: atSchema,
  via: z.enum(["voice", "typed", "tapped"]),
  text: z.string().min(1),
  /** Said to someone else in the room, not to Abacor: kept, never a note or an answer. */
  aside: z.literal(true).optional(),
});
export type SaidLine = z.infer<typeof saidLineSchema>;

/**
 * A note the person added at a moment after the recording ended (ENG-1029): their own words too,
 * placed at `at` on the clock and written at `writtenAt`.
 */
export const noteLineSchema = z.strictObject({
  kind: z.literal("note"),
  seq: z.int().positive(),
  at: atSchema,
  text: z.string().min(1),
  writtenAt: atSchema,
});
export type NoteLine = z.infer<typeof noteLineSchema>;

/** The person's own words on the clock: what they said or typed, and the notes they added. */
export const personLineSchema = z.discriminatedUnion("kind", [saidLineSchema, noteLineSchema]);
export type PersonWords = z.infer<typeof personLineSchema>;

/**
 * One answer a question was given; an undone one stays, marked, and a later one counts. One whose
 * input was removed (ENG-1029) is left out.
 */
export const packageAnswerSchema = z.strictObject({
  /** The tape entry that made it the answer. */
  seq: z.int().positive(),
  /** The person's input it came from, and when it was given: a correction's moment on the clock. */
  input: z.int().positive(),
  at: atSchema,
  words: z.string().min(1),
  via: z.enum(["voice", "typed", "tapped", "screen"]),
  /** Abacor's reading of it: never shown as something the person said. */
  took: z.string().min(1),
  undone: z.boolean(),
  /** Given in the package after the end, as a correction (ENG-1029). */
  later: z.boolean(),
  /** The answer it corrected, and the one that corrected it, by `seq`. */
  corrects: z.int().positive().nullable(),
  correctedBy: z.int().positive().nullable(),
});
export type PackageAnswer = z.infer<typeof packageAnswerSchema>;

export const packageQuestionSchema = z.strictObject({
  id: z.string().min(1),
  text: z.string().min(1),
  origin: questionOriginSchema,
  /** What prompted it, as Abacor put it. */
  evidence: z.string(),
  likely: z.array(z.string()),
  state: z.enum(["open", "asked", "passed", "held", "answered", "closed"]),
  /** When it entered the pool. */
  raisedAt: atSchema,
  /** Every time Abacor asked it, oldest first; empty for one it never asked. */
  askedAt: z.array(atSchema),
  answers: z.array(packageAnswerSchema),
});
export type PackageQuestion = z.infer<typeof packageQuestionSchema>;

/**
 * What the package does not have, shown so nobody wonders: the recording was paused; the person was
 * in a password or card field (`covered`); or in something not shared with Abacor (`unshared`,
 * ENG-1031), named by its app; or the person took the stretch out afterward (`removed`, ENG-1029).
 */
export const packageGapSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("paused"), from: atSchema, to: atSchema }),
  z.strictObject({ kind: z.literal("covered"), from: atSchema, to: atSchema }),
  z.strictObject({
    kind: z.literal("unshared"),
    from: atSchema,
    to: atSchema,
    app: z.string(),
  }),
  /** Everything in it was taken out after the end (ENG-1029). */
  z.strictObject({ kind: z.literal("removed"), from: atSchema, to: atSchema }),
]);
export type PackageGap = z.infer<typeof packageGapSchema>;

/** One part the person took out after the end (ENG-1029), as the log of removals shows it. */
export const packageRemovedPartSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("screen"), id: z.string().min(1) }),
  z.strictObject({ kind: z.literal("action"), id: z.string().min(1) }),
  z.strictObject({ kind: z.literal("said"), seq: z.int().positive() }),
  z.strictObject({ kind: z.literal("range"), from: atSchema, to: atSchema }),
]);
export type PackageRemovedPart = z.infer<typeof packageRemovedPartSchema>;

/** A removal, when it was made, and how many notes and answers Abacor dropped with it. */
export const packageRemovalSchema = z.strictObject({
  seq: z.int().positive(),
  at: atSchema,
  part: packageRemovedPartSchema,
  droppedNotes: z.int().nonnegative(),
  droppedAnswers: z.int().nonnegative(),
});
export type PackageRemoval = z.infer<typeof packageRemovalSchema>;

export const recordingPackageSchema = z.strictObject({
  recording: recordingViewSchema,
  /** Where the clock starts. */
  startedAt: atSchema,
  /** Where it ends: the recording's end, or now while it is going. */
  endsAt: atSchema,
  screens: z.array(packageScreenSchema),
  apps: z.array(appSpanSchema),
  actions: z.array(packageActionSchema),
  /** The person's own words, on the clock: what they said or typed, and the notes added later. */
  said: z.array(personLineSchema),
  questions: z.array(packageQuestionSchema),
  gaps: z.array(packageGapSchema),
  /** What the person took out after the end, oldest first (ENG-1029). */
  removals: z.array(packageRemovalSchema),
});
export type RecordingPackage = z.infer<typeof recordingPackageSchema>;

/** One line of the trace as the API keeps it. */
export type PackageTraceLine = {
  id: string;
  /**
   * The sender's key. The desktop app's keys carry a counter after the time, so two lines of the
   * same millisecond (a browser's app and then its page) keep the order they happened in.
   */
  key: string;
  at: number;
  event: TraceEvent;
  screenId: string | null;
};

const ASKED: ReadonlySet<string> = new Set(["asked", "retried", "picked", "cut-in", "answer-now"]);

type Stretch = { from: number; to: number };

/**
 * The package a recording's records make. `tape` is the whole tape, in order; `screens` the ready
 * screens that were not removed; `trace` the trace lines; `now` ends the clock of a recording still
 * going.
 */
export function foldPackage(input: {
  recording: RecordingView;
  tape: readonly TapeEntry[];
  screens: readonly ScreenView[];
  trace: readonly PackageTraceLine[];
  now: number;
}): RecordingPackage {
  const session = replay(input.tape);
  const atOf = new Map(input.tape.map((entry) => [entry.seq, entry.at]));
  const sorted = input.screens.toSorted((a, b) => a.at - b.at);
  const lines = input.trace.toSorted(
    (a, b) => a.at - b.at || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
  const startedAt = session.startedAt ?? input.tape[0]?.at ?? Date.parse(input.recording.createdAt);
  const last = Math.max(
    startedAt,
    input.tape.at(-1)?.at ?? 0,
    sorted.at(-1)?.at ?? 0,
    lines.at(-1)?.at ?? 0,
  );
  // An ended recording's clock stops at its end: a look at it afterward (`person.view`) lands on
  // the tape later but records nothing. A going one runs to now, or to what arrived last.
  const endsAt = session.endedAt ?? Math.max(input.now, last);
  // Capture may still deliver what the device kept after Finish, before it heard of the end (its
  // grant lasts a grace period). Nothing after the end is part of the recording.
  const within = <T extends { at: number }>(items: T[]) =>
    session.endedAt === null ? items : items.filter((item) => item.at <= endsAt);
  // What the person took out afterward is left out (ENG-1029): a screen or an action by its ID,
  // and everything in a removed stretch.
  const removed = removedOf(session.removals.map((r) => r.part));
  const screens = within(sorted).filter((s) => !removed.screens.has(s.id) && !removed.at(s.at));
  // Apps and pages are laid out from every line not removed by its ID, and only then cut where a
  // stretch was removed (it is a gap, below): a move to another app inside the stretch still says
  // where the evidence after it happened. Actions in a removed stretch are left out.
  const context = within(lines).filter((l) => !removed.actions.has(l.id));
  const trace = context.filter((l) => !removed.at(l.at));

  const gaps = gapsOf(input.tape, session, endsAt);
  const away = gaps.filter((gap) => gap.kind !== "covered");
  const traced = spansFromTrace(context, endsAt);
  const apps = clip(traced.length > 0 ? traced : spansFromScreens(screens, endsAt), away);

  return {
    recording: input.recording,
    startedAt,
    endsAt,
    screens: screens.map((screen) => ({
      ...screen,
      reason: CAPTURE_TRIGGER_REASONS[screen.trigger],
      flagged: !screen.checked,
    })),
    apps,
    actions: trace.flatMap((line) => {
      const event = line.event;
      return event.type === "app" || event.type === "page"
        ? []
        : [{ id: line.id, at: line.at, screenId: line.screenId, event }];
    }),
    said: [
      ...session.transcript.flatMap((line) =>
        line.who === "person" && !line.removed
          ? [
              {
                kind: "said" as const,
                seq: line.seq,
                at: line.at,
                via: line.via,
                text: line.text,
                ...(line.aside ? { aside: true as const } : {}),
              },
            ]
          : [],
      ),
      ...session.noted.flatMap((note) =>
        note.removed
          ? []
          : [
              {
                kind: "note" as const,
                seq: note.seq,
                at: note.at,
                text: note.text,
                writtenAt: note.writtenAt,
              },
            ],
      ),
    ].toSorted((a, b) => a.at - b.at || a.seq - b.seq),
    questions: session.questions.map((q) => ({
      id: q.id,
      text: q.text,
      origin: q.origin,
      evidence: q.evidence,
      likely: q.likely,
      state: q.state,
      raisedAt: atOf.get(q.raisedAt) ?? startedAt,
      askedAt: session.ledger.flatMap((l) =>
        l.questionId === q.id && ASKED.has(l.why) ? [atOf.get(l.seq) ?? startedAt] : [],
      ),
      answers: q.answers.flatMap((a) =>
        a.removed
          ? []
          : [
              {
                seq: a.receipt,
                input: a.input,
                at: a.moment ?? atOf.get(a.input) ?? a.at,
                words: a.words,
                via: a.via,
                took: a.took,
                undone: a.undone,
                later: a.moment !== undefined,
                corrects: a.corrects ?? null,
                correctedBy: a.correctedBy ?? null,
              },
            ],
      ),
    })),
    gaps,
    removals: session.removals.map((r) => ({
      seq: r.seq,
      at: r.at,
      part: removedView(r.part),
      droppedNotes: r.dropped.notes.length,
      droppedAnswers: r.dropped.answers.length,
    })),
  };
}

/** The parts taken out (ENG-1029): screens and trace lines by ID, and whether a time is in a stretch. */
function removedOf(parts: readonly RemovedPart[]) {
  const screens = new Set<string>();
  const actions = new Set<string>();
  const ranges: Stretch[] = [];
  for (const part of parts) {
    if (part.kind === "screen") screens.add(part.id);
    else if (part.kind === "action") actions.add(part.id);
    else if (part.kind === "range") ranges.push({ from: part.from, to: part.to });
  }
  const at = (time: number) => ranges.some((r) => time >= r.from && time <= r.to);
  return { screens, actions, at };
}

/** A removed part as the log shows it: without the tape entry the API found for it. */
function removedView(part: RemovedPart): PackageRemovedPart {
  switch (part.kind) {
    case "screen":
    case "action":
      return { kind: part.kind, id: part.id };
    case "said":
      return { kind: "said", seq: part.seq };
    case "range":
      return { kind: "range", from: part.from, to: part.to };
  }
}

/**
 * The stretches the recording was paused, the person was in a covered field, or in something not
 * shared, from the tape's entries that the session took (a dropped pause paused nothing). Every
 * time the session ended (`Session.ended`: Finish or the hour limit, which a later Answer now may
 * reopen) closes what was open then; pausing also leaves a covered field. A stretch still open
 * runs to the end.
 */
function gapsOf(tape: readonly TapeEntry[], session: Session, endsAt: number): PackageGap[] {
  const dropped = new Set(session.dropped.map((d) => d.seq));
  const gaps: PackageGap[] = [];
  let paused: number | null = null;
  let covered: number | null = null;
  const leaveCovered = (at: number) => {
    if (covered !== null) gaps.push({ kind: "covered", from: covered, to: at });
    covered = null;
  };
  const endings = new Map(session.ended.map((e) => [e.seq, e.at]));
  for (const { seq, at, event } of tape) {
    // The entry that ended the session may itself be dropped (one that came past the hour).
    const ended = endings.get(seq);
    if (ended !== undefined) {
      if (paused !== null) gaps.push({ kind: "paused", from: paused, to: ended });
      paused = null;
      leaveCovered(ended);
    }
    if (dropped.has(seq)) continue;
    switch (event.type) {
      case "person.pause":
        leaveCovered(at);
        paused ??= at;
        break;
      case "person.resume":
      case "person.answerNow":
        if (paused !== null) gaps.push({ kind: "paused", from: paused, to: at });
        paused = null;
        break;
      case "screen.masked":
        if (event.masked) covered ??= at;
        else leaveCovered(at);
        break;
      default:
        break;
    }
  }
  const end = session.endedAt ?? endsAt;
  if (paused !== null) gaps.push({ kind: "paused", from: paused, to: end });
  leaveCovered(end);
  for (const span of session.unshared) {
    gaps.push({
      kind: "unshared",
      from: span.from,
      to: span.to ?? end,
      app: span.app,
    });
  }
  for (const { part } of session.removals) {
    if (part.kind === "range") gaps.push({ kind: "removed", from: part.from, to: part.to });
  }
  return gaps.toSorted((a, b) => a.from - b.from);
}

/**
 * Apps and pages from the trace: each `app` line begins a stretch; a `page` line begins another in
 * the same app, unless the stretch had no page yet, which it fills in.
 */
function spansFromTrace(trace: readonly PackageTraceLine[], endsAt: number): AppSpan[] {
  // Each span runs to the next one's start; the last to the end.
  const spans: AppSpan[] = [];
  for (const { at, event } of trace) {
    const open = spans.at(-1);
    if (event.type === "app") {
      spans.push({
        from: at,
        to: endsAt,
        app: event.app,
        window: event.window,
        page: null,
      });
    } else if (event.type === "page" && open) {
      if (open.page === null) open.page = event.address;
      else if (open.page !== event.address) spans.push({ ...open, from: at, page: event.address });
    } else continue;
    const before = spans.at(-2);
    if (before && spans.at(-1) !== open) before.to = at;
  }
  return spans.filter((span) => span.to > span.from);
}

/** Apps and pages from the screens' contexts, for a recording kept before the trace (ENG-1027). */
function spansFromScreens(screens: readonly ScreenView[], endsAt: number): AppSpan[] {
  const spans: AppSpan[] = [];
  for (const { at, context } of screens) {
    const page = context.url ?? null;
    const open = spans.at(-1);
    if (open && open.app === context.app && open.window === context.window && open.page === page) {
      continue;
    }
    if (open) open.to = at;
    spans.push({
      from: at,
      to: endsAt,
      app: context.app,
      window: context.window,
      page,
    });
  }
  return spans.filter((span) => span.to > span.from);
}

/** The spans with the stretches the person was away (paused, not shared) cut out of them. */
function clip(spans: readonly AppSpan[], away: readonly Stretch[]): AppSpan[] {
  return spans.flatMap((span) => {
    let pieces: AppSpan[] = [span];
    for (const gap of away) {
      pieces = pieces.flatMap((piece) => {
        if (gap.to <= piece.from || gap.from >= piece.to) return [piece];
        const kept: AppSpan[] = [];
        if (gap.from > piece.from) kept.push({ ...piece, to: gap.from });
        if (gap.to < piece.to) kept.push({ ...piece, from: gap.to });
        return kept;
      });
    }
    return pieces;
  });
}
